import React, { FC, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { OrganisationDetailsWidget } from '../../components/organisation-details-widget'
import { CompanyUsersWidget } from '../../components/company-users-widget'
import {
    ChargePointUser,
    MeasurementDevicesGetModel,
    NfcTag,
    useCompanyDeleteCompanyMutation,
    useCompanyGetChargePointsPagedQuery,
    useCompanyGetCompanyByIdQuery,
    useCompanyGetDeviceByCompanyIdQuery,
    useCompanyGetLoadbalancerGroupsPagedQuery,
    useCompanyGetNfcTagsQuery,
    useCompanyGetUsersQuery,
    useCompanyRemoveMeasurementDeviceMutation,
    useCompanyRemoveUserMutation,
    useNfcTagDeleteMutation,
} from '../../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import useText from '../../../common/hooks/useText'
import { AddUserToOrganisationModal } from '../../components/add-user-to-organisation-modal'
import { SupportPropertiesWidget } from '../../components/support-properties-widget'
import { CompanyRfidWidget } from '../../components/company-rfid-widget'
import { RfidTagModal } from '../../../common/components/rfid-tag-modal'
import { ChargingSessionWidget } from '../../components/charging-session-widget'
import { CompanyChargersWidget } from '../../../lib/company-chargers-widget'
import { LoadbalancerGroupsWidget } from '../../../common/features/loadbalancer-group/loadbalancer-groups-widget'
import { CompanyMeasurementdevicesWidget } from '../../components/company-measurementdevices-widget'
import { AddMeasurementDeviceModal } from '../../components/add-measurement-device-modal/add-measurement-device-modal'
import { Button } from '../../../common/components/Button'
import ConfirmationModal from '../../../ui/ConfirmationModal'

export const OrganisationPage: FC = () => {
    const text = useText()
    const navigate = useNavigate()
    const { companyId } = useParams()
    const [addUserModalIsOpened, setAddUserModalIsOpened] = useState(false)
    const [addNewRfidTagModalIsOpened, setAddNewRfidTagModalIsOpened] = useState(false)
    const [addNewMeasurementDeviceModalIsOpened, setAddNewMeasurementDeviceModalIsOpened] = useState(false)
    const [deleteOrganisationModalIsOpened, setDeleteOrganisationModalIsOpened] = useState(false)
    const [rfidTag, setRfidTag] = useState<NfcTag>()
    const [currentPage, setCurrentPage] = useState(0)

    const { data: organisation } = useCompanyGetCompanyByIdQuery({ companyId }, { skip: !companyId })
    const { data: nfcTags = [] } = useCompanyGetNfcTagsQuery({ companyId }, { skip: !companyId })
    const { data: users = [] } = useCompanyGetUsersQuery({ companyId }, { skip: !companyId })
    const { data: measurementDevices = [] } = useCompanyGetDeviceByCompanyIdQuery({ companyId }, { skip: !companyId })
    const { data: chargePoints } = useCompanyGetChargePointsPagedQuery(
        { page: currentPage, noOfItems: 15, companyId, CompanyId: companyId },
        { skip: !companyId }
    )

    const [removeUser] = useCompanyRemoveUserMutation()
    const [removeTag] = useNfcTagDeleteMutation()
    const [removeMeasurementDevice] = useCompanyRemoveMeasurementDeviceMutation()
    const [deleteOrganisation] = useCompanyDeleteCompanyMutation()

    const { data: loadBalancerGroups, isFetching: loadingGroups } = useCompanyGetLoadbalancerGroupsPagedQuery(
        {
            companyId: companyId,
        },
        {
            skip: !companyId,
        }
    )
    const handleRemoveTag = (rfidTag: NfcTag) => {
        if (!confirm(text('lblConfirmDeletingRFIDTag'))) return

        if (rfidTag && rfidTag.id) {
            removeTag({ nfcTagId: rfidTag.id })
                .unwrap()
                .then(() => notifySuccess(text('msgRFIDRemoved')))
                .catch(() => notifyError(text('msgRFIDNotRemoved')))
        } else {
            notifyError(text('msgRFIDNotRemoved'))
        }
    }

    const handleRemoveUser = (user: ChargePointUser) => {
        if (user && user.id) {
            removeUser({ companyId, userId: user.id })
                .unwrap()
                .then(() => notifySuccess(text('msgUserRemoveFromOrg')))
                .catch((error) => {
                    const errormessage = error?.data.messageCode
                    if (
                        errormessage === 'msgUserWithMDCantBeRemovedFromCompany' ||
                        errormessage === 'msgUserWithTagsCantBeRemovedFromCompany'
                    ) {
                        notifyError(text(errormessage))
                    } else {
                        notifyError(text('msgUserNotRemovedFromOrg'))
                    }
                })
        } else {
            notifyError(text('msgUserRemoveFromOrgError'))
        }
    }

    const handleRemoveMeasurementDevice = (device: MeasurementDevicesGetModel) => {
        if (device && device.id) {
            removeMeasurementDevice({ companyId, measurementDeviceId: device.id })
                .unwrap()
                .then(() => notifySuccess(text('msgDeviceRemoved')))
                .catch(() => notifyError(text('msgMeasurementDeviceNotRemoved')))
        } else {
            notifyError(text('msgMeasurementDeviceNotRemoved'))
        }
    }

    const handleDeleteOrganisation = () => {
        if (companyId) {
            deleteOrganisation({ companyId })
                .unwrap()
                .then(() => {
                    navigate('/companies')
                    notifySuccess(text('msgDeletedOrganisation'))
                })
                .catch(() => {
                    notifyError(text('msgDeleteCompanyErrorAlert'))
                    setDeleteOrganisationModalIsOpened(false)
                })
        }
    }

    return (
        <PageLayoutContainer>
            <div className="flex justify-content-end">
                <Button
                    size="sm"
                    id="delete-organisation"
                    label={text('btnDeleteOrganisation')}
                    onClick={() => setDeleteOrganisationModalIsOpened(true)}
                />
            </div>
            <div className="col-12">
                <div className="grid">
                    <div className="col-6">
                        <OrganisationDetailsWidget organisationId={companyId} />
                        <CompanyUsersWidget
                            openUserModal={() => setAddUserModalIsOpened(true)}
                            users={users}
                            removeUser={handleRemoveUser}
                        />
                        <CompanyRfidWidget
                            companyId={companyId}
                            nfcTags={nfcTags}
                            openNfcTag={(tag) => {
                                setAddNewRfidTagModalIsOpened(true)
                                setRfidTag(tag)
                            }}
                            removeTag={handleRemoveTag}
                        />
                        <CompanyMeasurementdevicesWidget
                            measurementDevices={measurementDevices}
                            removeMeasurementDevice={handleRemoveMeasurementDevice}
                            openMeasurementModal={() => setAddNewMeasurementDeviceModalIsOpened(true)}
                        />
                    </div>
                    <div className="col-6">
                        <SupportPropertiesWidget company={organisation} />
                        <ChargingSessionWidget companyId={companyId} />
                        <CompanyChargersWidget
                            companyId={companyId}
                            chargePoints={chargePoints?.items ?? []}
                            loadingStatus={false}
                            paginationData={{
                                totalCount: chargePoints?.totalCount ?? 0,
                                pageIndex: currentPage,
                                pageSize: chargePoints?.pageSize ?? 0,
                            }}
                            setPageIndex={setCurrentPage}
                        />
                        <LoadbalancerGroupsWidget
                            loadBalancerGroups={loadBalancerGroups}
                            loadingGroups={loadingGroups}
                        />
                    </div>
                </div>
            </div>
            {addNewRfidTagModalIsOpened && (
                <RfidTagModal
                    onClose={() => {
                        setAddNewRfidTagModalIsOpened(false)
                        setRfidTag(undefined)
                    }}
                    rfidTag={rfidTag}
                />
            )}
            {addUserModalIsOpened && (
                <AddUserToOrganisationModal companyId={companyId} onClose={() => setAddUserModalIsOpened(false)} />
            )}

            {addNewMeasurementDeviceModalIsOpened && (
                <AddMeasurementDeviceModal
                    onClose={() => setAddNewMeasurementDeviceModalIsOpened(false)}
                    companyId={companyId}
                />
            )}
            {deleteOrganisationModalIsOpened && (
                <ConfirmationModal
                    title={text('btnDeleteOrganisation')}
                    message={text('msgDeleteCompanyConfirmation')}
                    onConfirm={handleDeleteOrganisation}
                    onClose={() => setDeleteOrganisationModalIsOpened(false)}
                />
            )}
        </PageLayoutContainer>
    )
}
